import React, { useEffect, useState, useCallback } from "react";
import { Popconfirm, Switch, Space, Table, Checkbox, Avatar, Tag } from "antd";
import { EditTwoTone, DeleteTwoTone, UserOutlined, EyeTwoTone, MailOutlined, PhoneOutlined, CalendarOutlined, IdcardOutlined, BookOutlined, HomeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import SubHeader from "../../components/SubHeader/SubHeader";
import SearchInput from "../../components/SearchInput/SearchInput";
import CustomTable from "../../components/CustomTable/CustomTable";
import CustomModal from "../../components/CustomModal/CustomModal";
import Loader from "../../components/Loader/Loader";
import ActionGroup from "../../components/ActionGroup/ActionGroup";
import ActionIcon from "../../components/ActionIcon/ActionIcon";
import { errorToast, successToast } from "../../services/ToastHelper";
import { postApi, getApi } from "../../utils/apiService";
import {
    GET_ALL_STAFFS,
    DELETE_STAFF,
    GET_STAFF_BY_ID,
    CHANGE_STAFF_STATUS,
    GET_ALL_MODULES
} from "../../utils/apiPath";
import dayjs from "dayjs";
import "./ManageTeam.scss";

const ManageTeam = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchString, setSearchString] = useState("");

    // View Details Modal state
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingStaff, setViewingStaff] = useState(null);

    // Modules list for permissions (still needed for view details labels)
    const [modulesList, setModulesList] = useState([]);

    const fetchModules = async () => {
        const res = await getApi(GET_ALL_MODULES);
        const data = Array.isArray(res) ? res : (res?.data || []);
        setModulesList(data);
    };

    const fetchStaffList = useCallback(async () => {
        setIsLoading(true);
        const { statusCode, data, message } = await getApi(GET_ALL_STAFFS, {
            params: {
                searchString,
                pageIndex,
                pageSize
            }
        });
        if (statusCode === 200) {
            setStaffList(data || []);
            setTotalRecords(data?.length || 0);
        } else {
            errorToast(message);
        }
        setIsLoading(false);
    }, [searchString, pageIndex, pageSize]);

    useEffect(() => {
        fetchStaffList();
    }, [fetchStaffList]);

    useEffect(() => {
        fetchModules();
    }, []);

    const handleSearch = (value) => {
        setSearchString(value);
        setPageIndex(0);
    };

    const handlePageChange = (page, size) => {
        setPageIndex(page - 1);
        setPageSize(size);
    };

    const handleStatusChange = async (teamId) => {
        setIsLoading(true);
        const { statusCode, message } = await postApi(CHANGE_STAFF_STATUS, { teamId });
        if (statusCode === 200) {
            successToast(message);
            fetchStaffList();
        } else {
            errorToast(message);
        }
        setIsLoading(false);
    };

    const handleDelete = async (teamId) => {
        setIsLoading(true);
        const { statusCode, message } = await postApi(DELETE_STAFF, { teamId });
        if (statusCode === 200) {
            successToast(message);
            fetchStaffList();
        } else {
            errorToast(message);
        }
        setIsLoading(false);
    };

    const handleViewDetails = async (record) => {
        setIsLoading(true);
        const { statusCode, data, message } = await postApi(GET_STAFF_BY_ID, { teamId: record.teamId });
        if (statusCode === 200) {
            // Map permissions to include labels for display
            const mappedPermissions = modulesList.map(m => {
                const existing = data.permissions?.find(p => p.sectionId === m.value);
                return {
                    sectionId: m.value,
                    label: m.label,
                    canView: existing?.canView || false,
                    canAdd: existing?.canAdd || false,
                    canEdit: existing?.canEdit || false,
                    canDelete: existing?.canDelete || false
                };
            });

            setViewingStaff({
                ...data,
                permissions: mappedPermissions
            });
            setIsViewModalOpen(true);
        } else {
            errorToast(message);
        }
        setIsLoading(false);
    };

    const handlePermissionChange = (sectionId, field, checked) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.map(p =>
                p.sectionId === sectionId ? { ...p, [field]: checked } : p
            )
        }));
    };

    const columns = [
        {
            title: "Staff",
            key: "staff",
            render: (_, record) => (
                <Space>
                    <Avatar src={record.profileImage} icon={<UserOutlined />} />
                    <div>
                        <div style={{ fontWeight: 600 }}>{record.name}</div>
                        <div style={{ fontSize: "12px", color: "#8c8c8c" }}>{record.designation}</div>
                    </div>
                </Space>
            )
        },
        {
            title: "Email",
            dataIndex: "emailId",
            key: "emailId"
        },
        {
            title: "Phone",
            dataIndex: "phoneNumber",
            key: "phoneNumber"
        },
        {
            title: "Department",
            dataIndex: "department",
            key: "department"
        },
        {
            title: "Status",
            key: "status",
            render: (_, record) => (
                <Popconfirm
                    title={`${record.isActive ? "Disable" : "Enable"} this staff?`}
                    onConfirm={() => handleStatusChange(record.teamId)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Switch checked={record.isActive} size="small" />
                </Popconfirm>
            )
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <ActionGroup>
                    <ActionIcon
                        title="View Details"
                        icon={<EyeTwoTone twoToneColor={["#0e82fd", "#e6f4ff"]} />}
                        onClick={() => handleViewDetails(record)}
                    />
                    <ActionIcon
                        title="Edit"
                        icon={<EditTwoTone twoToneColor={["#52c41a", "#f6ffed"]} />}
                        onClick={() => navigate(`/edit-staff/${record.teamId}`)}
                    />
                    <ActionIcon
                        title="Delete"
                        icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                        confirm={{
                            enabled: true,
                            title: `Delete "${record.name}"?`,
                            okText: "Delete",
                            cancelText: "Cancel",
                            onConfirm: () => handleDelete(record.teamId),
                        }}
                    />
                </ActionGroup>
            )
        }
    ];

    return (
        <div className="manage-team">
            {isLoading && <Loader />}

            <SubHeader
                title="Manage Team"
                showBack={false}
                showRight={true}
                rightActionLabel="Add New Staff"
                onRightClick={() => navigate("/add-staff")}
                rightExtra={
                    <SearchInput
                        placeholder="Search by name, email..."
                        onDebouncedChange={handleSearch}
                    />
                }
            />

            <div className="table-container">
                <CustomTable
                    columns={columns}
                    dataSource={staffList}
                    total={totalRecords}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    rowKey="teamId"
                />
            </div>

            <CustomModal
                open={isViewModalOpen}
                title="Staff Details"
                onClose={() => setIsViewModalOpen(false)}
                width={700}
                showPrimary={false}
                dangerText="Close"
            >
                {viewingStaff && (
                    <div className="staff-details-view">
                        <div className="details-header">
                            <Avatar size={80} src={viewingStaff.profileImage} icon={<UserOutlined />} />
                            <div className="header-info">
                                <h3>{viewingStaff.name}</h3>
                                <p className="designation">{viewingStaff.designation}</p>
                                <Tag color="blue">{viewingStaff.department}</Tag>
                            </div>
                        </div>

                        <div className="details-grid">
                            <div className="detail-item">
                                <label><MailOutlined /> Email</label>
                                <span>{viewingStaff.emailId}</span>
                            </div>
                            <div className="detail-item">
                                <label><PhoneOutlined /> Phone</label>
                                <span>{viewingStaff.phoneNumber}</span>
                            </div>
                            <div className="detail-item">
                                <label><HomeOutlined /> Branch</label>
                                <span>{viewingStaff.branches?.map(b => b.branchName).join(", ") || "-"}</span>
                            </div>
                            <div className="detail-item">
                                <label><IdcardOutlined /> Gender</label>
                                <span>{viewingStaff.gender || "-"}</span>
                            </div>
                            <div className="detail-item">
                                <label><CalendarOutlined /> Date of Birth</label>
                                <span>{viewingStaff.dateOfBirth ? dayjs(viewingStaff.dateOfBirth).format("DD MMMM, YYYY") : "-"}</span>
                            </div>
                            <div className="detail-item">
                                <label><BookOutlined /> Grades</label>
                                <span>{viewingStaff.gradeIds?.map(id => `Grade ${id}`).join(", ") || "-"}</span>
                            </div>
                        </div>

                        <div className="permissions-view">
                            <h4>Permissions</h4>
                            <Table
                                dataSource={viewingStaff.permissions}
                                pagination={false}
                                size="small"
                                bordered
                                rowKey="sectionId"
                                columns={[
                                    { title: "Module", dataIndex: "label", key: "label" },
                                    { 
                                        title: "All", 
                                        key: "all", 
                                        render: (_, r) => (r.canView && r.canAdd && r.canEdit && r.canDelete) ? "✅" : "❌" 
                                    },
                                    { title: "View", key: "canView", render: (_, r) => r.canView ? "✅" : "❌" },
                                    { title: "Add", key: "canAdd", render: (_, r) => r.canAdd ? "✅" : "❌" },
                                    { title: "Edit", key: "canEdit", render: (_, r) => r.canEdit ? "✅" : "❌" },
                                    { title: "Delete", key: "canDelete", render: (_, r) => r.canDelete ? "✅" : "❌" },
                                ]}
                            />
                        </div>
                    </div>
                )}
            </CustomModal>
        </div>
    );
};

export default ManageTeam;